import { useState } from "react";
import { createCreditChargeAsync } from "src/api/credit/credit-api";
import { ContractCreditBucket } from "src/api/credit/credit-types";
import { useApiOperation } from "src/api/hooks";
import { BaseButton, BaseInput, BaseModal, BaseSelect } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { BaseInputNumberFormat } from "src/components/BaseInputNumberFormat";
import { useToast } from "src/recoil/toast/hook";
import { deleteCommas, inputPriceFormat, numberToStringWithComma } from "src/utils";
import CreditStatusTable from "./CreditStatusTable";

type ChargeType = "PAY" | "RECOVER"; // 지급 / 회수

const chargeTypeOptions = [
  { value: "PAY", label: "지급" },
  { value: "RECOVER", label: "회수" },
];

interface IProps {
  contractId: string;
  bucket: ContractCreditBucket;
  onClose: () => void;
  onSaved: () => void; // 저장 성공 시 (목록 갱신 + 닫기)
}

/**
 * 계약 충전금 관리 > 월별 충전금 내역 > 지급/회수 모달 (RCL23)
 * - 충전금 현황(현재 버킷) 표시 + 구분(지급/회수)·충전금·사유 입력
 * - 회수 시 잔여 충전금 초과 불가 (확인 클릭 시점 검증)
 * - 확인 전 더블체크 모달 (지급/회수 후 잔여 충전금 안내)
 */
const CreditChargeModal = ({ contractId, bucket, onClose, onSaved }: IProps) => {
  const { openToast } = useToast();
  const { executeAsync: createCreditCharge } = useApiOperation(createCreditChargeAsync);

  const [type, setType] = useState<ChargeType>("PAY");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [confirmModal, setConfirmModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // 중복 제출 방지

  const remaining = Number(bucket.remainingCredit ?? 0); // 현재 잔여 충전금
  const amountNum = Number(deleteCommas(amount)) || 0;
  const afterRemaining = type === "RECOVER" ? remaining - amountNum : remaining + amountNum;
  const typeLabel = type === "RECOVER" ? "회수" : "지급";

  const saveDisabled = amountNum <= 0 || !description.trim();

  // 확인 클릭 → 회수 초과 검증 후 더블체크 모달
  const onClickConfirm = () => {
    if (type === "RECOVER" && amountNum > remaining) {
      setErrorModal(true);
      return;
    }
    setConfirmModal(true);
  };

  // 더블체크 확인 → RCL23 호출 (회수는 음수)
  const onClickDoubleCheck = async () => {
    if (isSubmitting) return; // 중복 제출 방지
    setIsSubmitting(true);
    try {
      const chargedCredit = type === "RECOVER" ? -amountNum : amountNum;
      const res = await createCreditCharge({ contractId, bucketId: bucket.bucketId, body: { chargedCredit, description: description.trim() } });
      if (res?.data?.data) {
        openToast({ content: "정상적으로 처리되었습니다." });
        setConfirmModal(false);
        onSaved();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseAbstractModal isOpen>
      <>
        <section className="base-abstract-modal__title buttom-list-wrap">
          <h1>{bucket.yearMonth} 충전금 지급/회수</h1>
          <ul className="base-list">
            <li>
              추가 지급된 충전금은 이미 확정된 정산 내역에 소급 적용되지 않습니다. 과거에 발생한 후불 요금은 월말 청구서를 통해 조정할 수 있습니다.
            </li>
            <li>회수 후 잔여 충전금이 부족해지면 이후 예약 및 이용분에 후불 요금이 발생할 수 있습니다. 진행 중인 예약은 유지됩니다.</li>
          </ul>
        </section>

        <section className="base-abstract-modal__contents px30">
          {/* 충전금 현황 */}
          <section className="base-abstract-modal__contents-subtitle">
            <h2>충전금 현황</h2>
          </section>
          <div className="mb30">
            <CreditStatusTable bucket={bucket} />
          </div>

          {/* 충전금 지급/회수 입력 */}
          <section className="base-abstract-modal__contents-subtitle">
            <h2>충전금 지급/회수</h2>
          </section>
          <div className="base-table-wrap pl0" style={{ paddingRight: 0 }}>
            <div className="base-table sticky">
              <div className="header">
                <div className="base-table__tr" style={{ display: "flex" }}>
                  <div className="base-table__th" style={{ width: 150 }}>
                    <span>구분</span>
                  </div>
                  <div className="base-table__th" style={{ width: 395 }}>
                    <span>
                      <span className="text-red">*</span> 충전금
                    </span>
                  </div>
                  <div className="base-table__th" style={{ width: 395 }}>
                    <span>
                      <span className="text-red">*</span> 사유
                    </span>
                  </div>
                </div>
              </div>
              <div className="body">
                <div className="base-table__tr" style={{ display: "flex" }}>
                  <div className="base-table__td" style={{ width: 150 }}>
                    <BaseSelect
                      menuPosition="fixed"
                      menuPortalTarget={document.body}
                      stateOptions={chargeTypeOptions}
                      value={type}
                      setStateValue={(v: string) => setType(v as ChargeType)}
                    />
                  </div>
                  <div className="base-table__td" style={{ width: 395 }}>
                    <BaseInputNumberFormat
                      className="w-100"
                      type="text"
                      onChange={(text: string) => {
                        // 999999999 이상 입력 불가
                        if (Number(deleteCommas(text)) >= 999999999) return;
                        setAmount(text);
                      }}
                      value={inputPriceFormat(amount)}
                      name="chargeAmount"
                      placeholder={`${typeLabel} 충전금을 입력해주세요`}
                    />
                  </div>
                  <div className="base-table__td" style={{ width: 395 }}>
                    <BaseInput
                      className="w-100"
                      onChange={(v: string) => setDescription(v)}
                      value={description}
                      name="chargeDescription"
                      placeholder="사유를 입력해주세요"
                      maxLength={1000}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="base-abstract-modal__btn-wrap">
          <BaseButton title={"닫기"} className="color-white" onClick={onClose} />
          <BaseButton title={"확인"} disabled={saveDisabled} onClick={onClickConfirm} />
        </section>

        {/* 회수 초과 에러 모달 */}
        {errorModal && (
          <BaseModal
            isOpen
            title={`현재 잔여 충전금보다 큰 금액은\n 회수할 수 없습니다.`}
            btnRightTitle="확인"
            onClick={() => setErrorModal(false)}
            onClose={() => setErrorModal(false)}
          />
        )}

        {/* 더블체크 모달 */}
        {confirmModal && (
          <BaseModal
            isOpen
            title={`${numberToStringWithComma(amountNum)}원 ${typeLabel}하시겠습니까?`}
            btnLeftTitle="취소"
            btnRightTitle="확인"
            btnRightDisable={isSubmitting}
            onClick={onClickDoubleCheck}
            onClose={() => setConfirmModal(false)}
          >
            <p className="text-primary3">
              {typeLabel} 후 잔여 충전금 : {numberToStringWithComma(afterRemaining)}
            </p>
            <p className="text-primary3">이 작업은 되돌릴 수 없으며, 진행 중인 예약에 영향을 줄 수 있습니다.</p>
          </BaseModal>
        )}
      </>
    </BaseAbstractModal>
  );
};

export default CreditChargeModal;
